import { Board, DEFAULT_BOARD_ROWS } from "./black_hole_game.ts";

const STORAGE_KEY = "black-hole:in-progress-game";
const SMALL_BOARD_ROWS = 5;

interface StoredGameV1 {
    version: 1;
    rows: number;
    moves: [number, number][];
    aiPlayers: (1 | 2)[];
    playerName: string;
    selectedModelId: string;
    useBookOpponent: boolean;
    allowUndo: boolean;
    showHints: boolean;
    showHintValues: boolean;
    showCellPoints?: boolean;
}

export interface SavedGameSettings {
    aiPlayers: (1 | 2)[];
    playerName: string;
    selectedModelId: string;
    useBookOpponent: boolean;
    allowUndo: boolean;
    showHints: boolean;
    showHintValues: boolean;
    showCellPoints: boolean;
}

export interface RestoredGame extends SavedGameSettings {
    board: Board;
}

function storage(): Storage | null {
    try {
        return typeof window === "undefined" ? null : window.localStorage;
    } catch {
        return null;
    }
}

function movesFromBoard(board: Board): [number, number][] {
    const byPly = new Map<number, [number, number]>();
    for (let row = 0; row < board.rows; row++) {
        for (let col = 0; col <= row; col++) {
            const value = board.at(row, col);
            if (value === 0) continue;
            const ply = (Math.abs(value) - 1) * 2 + (value < 0 ? 1 : 0);
            byPly.set(ply, [row, col]);
        }
    }
    return Array.from({ length: board.filled }, (_, ply) => {
        const move = byPly.get(ply);
        if (!move) throw new Error("Board has an invalid move sequence");
        return move;
    });
}

function isSettings(value: unknown): value is StoredGameV1 {
    if (!value || typeof value !== "object") return false;
    const game = value as Partial<StoredGameV1>;
    const validPlayers = Array.isArray(game.aiPlayers) &&
        game.aiPlayers.length <= 2 &&
        game.aiPlayers.every(player => player === 1 || player === 2) &&
        new Set(game.aiPlayers).size === game.aiPlayers.length;
    return game.version === 1 &&
        (game.rows === SMALL_BOARD_ROWS || game.rows === DEFAULT_BOARD_ROWS) &&
        Array.isArray(game.moves) &&
        validPlayers &&
        typeof game.playerName === "string" &&
        typeof game.selectedModelId === "string" &&
        typeof game.useBookOpponent === "boolean" &&
        typeof game.allowUndo === "boolean" &&
        typeof game.showHints === "boolean" &&
        typeof game.showHintValues === "boolean" &&
        (typeof game.showCellPoints === "undefined" || typeof game.showCellPoints === "boolean");
}

export function loadInProgressGame(): RestoredGame | null {
    const target = storage();
    if (!target) return null;

    try {
        const raw = target.getItem(STORAGE_KEY);
        if (!raw) return null;
        const game: unknown = JSON.parse(raw);
        if (!isSettings(game)) throw new Error("Invalid saved game");

        let board = new Board(game.rows);
        for (const move of game.moves) {
            if (!Array.isArray(move) || move.length !== 2 ||
                !Number.isInteger(move[0]) || !Number.isInteger(move[1])) {
                throw new Error("Invalid saved move");
            }
            board = board.play(move[0], move[1]);
        }
        if (board.filled === 0 || board.isGameOver()) {
            throw new Error("Saved game is not in progress");
        }

        return {
            board,
            aiPlayers: game.aiPlayers,
            playerName: game.playerName,
            selectedModelId: game.selectedModelId,
            useBookOpponent: game.useBookOpponent,
            allowUndo: game.allowUndo,
            showHints: game.showHints,
            showHintValues: game.showHintValues,
            showCellPoints: game.showCellPoints ?? false,
        };
    } catch {
        try {
            target.removeItem(STORAGE_KEY);
        } catch {
            // Ignore storage access failures and start a fresh game.
        }
        return null;
    }
}

export function saveInProgressGame(board: Board, settings: SavedGameSettings): void {
    const target = storage();
    if (!target) return;

    try {
        const game: StoredGameV1 = {
            version: 1,
            rows: board.rows,
            moves: movesFromBoard(board),
            aiPlayers: settings.aiPlayers,
            playerName: settings.playerName,
            selectedModelId: settings.selectedModelId,
            useBookOpponent: settings.useBookOpponent,
            allowUndo: settings.allowUndo,
            showHints: settings.showHints,
            showHintValues: settings.showHintValues,
            showCellPoints: settings.showCellPoints,
        };
        target.setItem(STORAGE_KEY, JSON.stringify(game));
    } catch {
        // Persistence is best-effort; gameplay must still work when storage is unavailable.
    }
}

export function clearInProgressGame(): void {
    try {
        storage()?.removeItem(STORAGE_KEY);
    } catch {
        // Persistence is best-effort.
    }
}
